import React, { useState, useEffect, useRef, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button } from "reactstrap";
import MusicLinkTable from "../../components/musicLinkTable";
import Pagination from "../../components/pagination/pagination";
import TableModal from "../../components/tableModal";
import ViewLayout from "../../components/viewLayout";
import { useFetch } from "../../utils/fetchHook";

import { StateContext } from "../../utils/context";

import { getCookie } from "../../jwt/_helpers/cookie";
import TableHelper from "../../components/tableHelper";
import ModuleAccessDeniedAlert from "../../components/moduleAccessDeniedAlert";
import { deleteMusicLinkPage, setMusicLinkPages } from "../../redux/musicLinkPages/action";
import {
  getDeletedDemoMusicLinkIds,
  getDemoMusicLinkRows,
  removeDemoMusicLinkRow,
  upsertDemoMusicLinkRow,
} from "../../utils/demoMusicLinkSessionStore";
import mockMusicLinksData from "../../data/mockData/musicLinksData";
import {
  enrichMusicLinkListRow,
  getMusicLinkMockExportPayload,
} from "../../utils/musicLinkDemoHelpers";
import {
  isBrowserSessionDemo,
  isDemoMode,
  isCopyMockDataEnabled,
} from "../../utils/demoConfig";
import { DEMO_URL_SHARING_NOTICE } from "../../utils/demoUrlSharingNotice";

const SEED_MUSIC_LINK_IDS = new Set(mockMusicLinksData.map(row => String(row.id)));

const mergeById = (baseRows, extraRows) => {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extraRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), { ...(map.get(String(row.id)) || {}), ...row });
    }
  });
  return Array.from(map.values());
};

const filterMusicLinkRowsByQuery = (rows, rawQuery) => {
  const queryText = String(rawQuery || "").trim().toLowerCase();
  if (!queryText) {
    return rows;
  }
  return (rows || []).filter(row =>
    [row?.release_name, row?.artist_name, row?.url_part2, row?.url]
      .some(value => String(value || "").toLowerCase().includes(queryText)),
  );
};

const MusicLink = () => {
  const dispatch = useDispatch();
  const reduxMusicLinkExtras = useSelector(state => state.musicLinkPages?.created || []);

  const [openModal, setToggleModal] = useState(false);
  const [dataModal, setDataModal] = useState(null);
  const [deleteBody, setDeleteBody] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorPut, setError] = useState(false);
  const [mockCopyNotice, setMockCopyNotice] = useState("");

  const [forceUpdate, setForce] = useState(1);

  const handlerValues = { setSuccess, setError, forceUpdate, setForce };
  const [tableData, setTableData] = useState([]);

  const didEff1 = useRef(false);
  const didEff2 = useRef(false);
  const [dataCount, setDataCount] = useState(0);
  const [currrentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [ordering, setOrdering] = useState({ order: "-", name: "created", sort: "created" });
  const [subUser, setSubUser] = useState("");
  const [subUserEndpoint, setSubUserEndpoint] = useState("");
  const [parentUserOnly, setParentUserOnly] = useState("");

  const MUSIC_LINK_URL = process.env.REACT_APP_MUSIC_LINK_URL;

  const token = getCookie("token");
  const { currentUser } = useContext(StateContext);

  const shouldUseDemoDelete = isDemoMode() || isBrowserSessionDemo();
  const shouldUseLocalPagination = shouldUseDemoDelete;
  const fetchPage = shouldUseLocalPagination ? 1 : currrentPage;
  const fetchPageSize = shouldUseLocalPagination ? 1000 : pageSize;

  const [urlparts] = useFetch("GET", "musiclinks_urlparts/", token);
  const [apiData, error, loading] = useFetch(
    "GET",
    `musiclinks/${subUserEndpoint}?ordering=${ordering.order + ordering.name}&page=${fetchPage}&page_size=${fetchPageSize}&search=${query}${parentUserOnly}`,
    token,
    false,
    forceUpdate,
  );

  const defaultSorted = [
    {
      dataField: ordering.sort,
      order: ordering.order === "-" ? "desc" : "asc",
    },
  ];

  const buildMusicLinkUrl = row => {
    const enriched = enrichMusicLinkListRow(row);
    const partSlug =
      (urlparts?.results || []).find(u => String(u.id) === String(enriched.url_part1))?.part ||
      enriched.url_part1_name ||
      "demo";
    return `${window.location.origin}/l/${partSlug}/${enriched.url_part2 || enriched.id}`;
  };

  const handleCopyMockData = () => {
    const payload = getMusicLinkMockExportPayload();
    navigator.clipboard
      .writeText(JSON.stringify(payload, null, 2))
      .then(() => setMockCopyNotice("Mock data copied to clipboard."))
      .catch(() => setMockCopyNotice("Could not copy mock data."));
  };

  const handleDemoDelete = () => {
    if (!dataModal?.id) {
      return;
    }
    removeDemoMusicLinkRow(dataModal.id);
    dispatch(deleteMusicLinkPage(dataModal.id));
    setTableData(prev => prev.filter(row => String(row?.id) !== String(dataModal.id)));
    setDataCount(prev => Math.max(prev - 1, 0));
    setToggleModal(false);
    setDeleteBody(false);
    setDataModal(null);
    setError(false);
    setSuccess(true);
  };

  useEffect(() => {
    if (apiData?.detail === "Invalid page.") {
      setCurrentPage(1);
      return;
    }

    if (!apiData) {
      return;
    }

    const apiRows = Array.isArray(apiData.results) ? apiData.results : [];
    const deletedIds = new Set(getDeletedDemoMusicLinkIds().map(String));
    const visibleApiRows = apiRows.filter(row => !deletedIds.has(String(row?.id)));
    if (Array.isArray(apiData.results)) {
      dispatch(setMusicLinkPages(apiData.results));
    }

    const sessionRows = getDemoMusicLinkRows();
    const mergedRows = mergeById(mergeById(mergeById(sessionRows, reduxMusicLinkExtras), visibleApiRows))
      .filter(row => !deletedIds.has(String(row?.id)))
      .map(enrichMusicLinkListRow)
      .sort((a, b) => {
        const left = new Date(b?.created || 0).getTime();
        const right = new Date(a?.created || 0).getTime();
        return left - right;
      });

    mergedRows.forEach(row => {
      if (!SEED_MUSIC_LINK_IDS.has(String(row?.id))) {
        upsertDemoMusicLinkRow(row);
      }
    });

    const filteredRows = filterMusicLinkRowsByQuery(mergedRows, query).map(row => ({
      ...row,
      musiclink_url: buildMusicLinkUrl(row),
    }));

    if (shouldUseLocalPagination) {
      const totalRows = filteredRows.length;
      const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
      const boundedPage = Math.min(currrentPage, totalPages);
      if (boundedPage !== currrentPage) {
        setCurrentPage(boundedPage);
        return;
      }
      const start = (boundedPage - 1) * pageSize;
      const end = start + pageSize;
      setTableData(filteredRows.slice(start, end));
      setDataCount(totalRows);
      return;
    }

    setTableData(filteredRows);
    setDataCount(Number(apiData?.count) || filteredRows.length);
  }, [
    apiData,
    currrentPage,
    dispatch,
    pageSize,
    query,
    reduxMusicLinkExtras,
    shouldUseLocalPagination,
    urlparts,
  ]);

  useEffect(() => {
    if (!didEff1.current) {
      didEff1.current = true;
      return;
    }
    if (shouldUseLocalPagination) {
      return;
    }
    setForce(prev => prev + 1);
  }, [currrentPage, ordering.order, ordering.name, pageSize, shouldUseLocalPagination, subUser]);

  useEffect(() => {
    if (!didEff2.current) {
      didEff2.current = true;
      return;
    }
    const timeout = setTimeout(() => {
      setForce(prev => prev + 1);
    }, 500);
    return () => clearTimeout(timeout);
  }, [query]);

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount,
    setCurrentPage,
    currrentPage,
  };

  const tableProps = {
    tableData,
    defaultSorted,
  };

  const showCopyMockData = isCopyMockDataEnabled();

  return (
    <>
      {showCopyMockData && (
        <Alert color="light" className="mb-3 py-2">
          <Button size="sm" color="primary" onClick={handleCopyMockData}>
            Copy mock data
          </Button>
          {mockCopyNotice && (
            <span className="text-muted ml-2">{mockCopyNotice}</span>
          )}
          <small className="d-block text-muted mt-1">
            Exports current music link rows as JSON (paste to update seed data).
          </small>
        </Alert>
      )}
      {!currentUser.music_link && !loading ? (
        <ModuleAccessDeniedAlert
          module="music_link"
          fallbackMessage="Currently this feature is only offered to few selected users but the good news is that you will also be able to use it really soon. Keep watching this space!"
        />
      ) : currentUser.music_link && loading ? (
        <TableHelper loading />
      ) : error ? (
        <TableHelper error />
      ) : (
        <div>
          <ViewLayout title={"Your music links list"} noteAppend={DEMO_URL_SHARING_NOTICE}>
            <MusicLinkTable
              {...tableProps}
              setToggleModal={setToggleModal}
              setDataModal={setDataModal}
              setDeleteBody={setDeleteBody}
              forceUpdate={forceUpdate}
              searchQuery={{ setQuery, query, ordering, setOrdering }}
              subUserData={{
                subUser,
                setSubUser,
                subUserEndpoint,
                setSubUserEndpoint,
                setParentUserOnly,
              }}
            />
          </ViewLayout>

          <Pagination pagination={paginationProps} />
        </div>
      )}

      {dataModal && (
        <TableModal
          openModal={openModal}
          setToggleModal={setToggleModal}
          dataModal={dataModal}
          values={handlerValues}
          title={
            deleteBody ? `Delete ${dataModal.release_name}?` : `Edit ${dataModal.name}?`
          }
          body={"Are you really sure you really want to delete this music-link?"}
          apiURL={`musiclinks/${dataModal.id}/`}
          bodyID={dataModal.id}
          deleteBody={deleteBody}
          onDeleteConfirm={shouldUseDemoDelete ? handleDemoDelete : undefined}
        />
      )}
      {success && <Alert color="success">Music Link has been removed!</Alert>}
      {errorPut && (
        <Alert color="danger">
          Something went wrong! Please refresh page and try again!
        </Alert>
      )}
    </>
  );
};

export default MusicLink;
